import { Fragment, useMemo } from 'react';

import {
  FilterAppliedProps,
  FilterComparer,
  FilterConfig,
  Filters,
  useCurrentFilter,
} from '@components/filters';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconLabel } from '@components/icons/label';

import { useStore } from 'models/Provider';

import { FilterActionButtons } from './filter-action-buttons';
import { FilterComparerMenu } from './filter-comparer-menu';

const LABEL_COMPARERS: Array<FilterComparer> = ['is', 'not_is', 'any_of', 'none_of'];

function FilterApplicationLabelsApplied({ data }: FilterAppliedProps) {
  const { t } = useStore();

  const valueLabel = Array.isArray(data.value)
    ? data.value.map((label) => t(`applicants.filters.label.${label}`))
    : t(`applicants.filters.label.${data.value as string}`);

  return (
    <Filters.AppliedFilter
      icon={<IconLabel />}
      category={t('applicants.filters.label.title')}
      value={valueLabel}
      comparer={{
        value: data.comparer!,
        label: t('comparers.' + data.comparer).replace('...', ''),
      }}
    />
  );
}

function FilterApplicationLabelsMenu() {
  const { t } = useStore();
  const { state, actions, isMultiComparer, hasValue } = useCurrentFilter();

  const filterValueItems = ['notified', 'sourced', 'manuallyadded', 'none'].map((item) => ({
    value: item,
    label: t('applicants.filters.label.' + item),
  }));

  return (
    <Filters.List>
      <Filters.ListItem
        heading
        label={t('applicants.filters.label.title')}
        icon={<IconArrowLeft />}
        onClick={() => {
          actions.unselectCategory();
        }}
      />

      {state.comparer && (
        <Filters.ListItem
          heading
          label={t('comparers.' + state.comparer).replace('...', '')}
          icon={<IconArrowLeft />}
          onClick={actions.unselectComparer}
        />
      )}

      <Filters.ListSeparator />

      {!state.comparer && (
        <Fragment>
          <Filters.ListItem
            label={t('applicants.filters.label.none')}
            onClick={() => {
              actions.applyFilter({ comparer: 'is', value: 'none' });
            }}
          />

          <Filters.ListSeparator />

          <FilterComparerMenu
            comparers={LABEL_COMPARERS}
            onComparerSelect={actions.selectComparer}
          />
        </Fragment>
      )}

      {state.comparer && (
        <Fragment>
          <Filters.FilterValueMenu
            items={filterValueItems}
            isMulti={isMultiComparer}
            onSetValue={actions.selectValue}
            state={state}
          />
          <FilterActionButtons disableApply={!hasValue} />
        </Fragment>
      )}
    </Filters.List>
  );
}

export function useFilterApplicationLabels(): FilterConfig {
  const { t } = useStore();

  return useMemo(
    () => ({
      category: 'label',
      label: t('applicants.filters.label.title'),
      icon: <IconLabel />,
      AppliedFilter: FilterApplicationLabelsApplied,
      Menu: FilterApplicationLabelsMenu,
      toQueryData: (data) => {
        switch (data.comparer) {
          case 'is':
            return { labels__term: data.value };
          case 'not_is':
          case 'none_of':
            return { labels__exclude: data.value };
          default:
            return { labels: data.value };
        }
      },
    }),
    [t]
  );
}
